import { effect, inject, Service, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DiscussionDto, UserApplications } from '@workspace/data-types';
import { ApplicationStore, NotificationStateService, UserStore } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { catchError, fromEvent, Observable, of } from 'rxjs';

@Service()
export class DiscussionStateService {
  private readonly api = inject(ApiService);
  private readonly userStore = inject(UserStore);
  private readonly applicationStore = inject(ApplicationStore);
  private readonly notificationState = inject(NotificationStateService);

  private readonly unreadDiscussionsCount = signal<number>(0);
  public readonly unreadCount = this.unreadDiscussionsCount.asReadonly();
  public readonly activeDiscussionId = signal<string | null>(null);

  constructor() {
    // Listen to chat message events pushed via SSE
    this.notificationState.chatMessageReceived
      .pipe(takeUntilDestroyed())
      .subscribe((metadata: Record<string, unknown>) => {
        const activeId = this.activeDiscussionId();
        if (activeId && metadata?.['discussionId'] === activeId) {
          return;
        }
        this.refresh();
      });

    // Run initial load and subsequent refreshes when user or application scope changes
    effect(() => {
      const user = this.userStore.current();
      const appName = this.applicationStore.name();
      if (user && appName) {
        untracked(() => this.refresh());
      } else {
        this.unreadDiscussionsCount.set(0);
      }
    });

    // Sync when coming back online
    fromEvent(window, 'online')
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.refresh());
  }

  public refresh() {
    const user = this.userStore.current();
    const appName = this.applicationStore.name();
    if (!user || !appName) {
      this.unreadDiscussionsCount.set(0);
      return;
    }

    let getDiscussions$: Observable<DiscussionDto[]>;
    if (appName === UserApplications.OWNER) {
      getDiscussions$ = this.api.business().etxe().discussions().owner().getDiscussions();
    } else if (appName === UserApplications.AGENT) {
      if (!user.profiles?.agent) {
        this.unreadDiscussionsCount.set(0);
        return;
      }
      getDiscussions$ = this.api.business().etxe().discussions().agent().getDiscussions();
    } else {
      this.unreadDiscussionsCount.set(0);
      return;
    }

    getDiscussions$.pipe(catchError(() => of<DiscussionDto[]>([]))).subscribe((discussions) => {
      let count = 0;
      for (const disc of discussions) {
        const me = disc.participants?.find((p) => p.participant.toString() === user._id.toString());
        if (me && me.unreadCount > 0) {
          count++;
        }
      }
      this.unreadDiscussionsCount.set(count);
    });
  }

  public updateUnreadCountFromList(discussions: DiscussionDto[]) {
    const user = this.userStore.current();
    if (!user) {
      this.unreadDiscussionsCount.set(0);
      return;
    }
    let count = 0;
    for (const disc of discussions) {
      const me = disc.participants?.find((p) => p.participant.toString() === user._id.toString());
      if (me && me.unreadCount > 0) {
        count++;
      }
    }
    this.unreadDiscussionsCount.set(count);
  }
}
